import { useMemo } from 'react';

import { useAuthorizationV2 } from 'components/common/Authorization/AuthHookV2';
import { useTeamAuthorizationV2 } from 'components/common/Authorization/AuthTeamHookV2';
import type {
  AuthorizationState,
  Team,
} from 'components/common/Authorization/types';

interface UseAuthorizationLogicParams {
  requiredPermission: string;
  team?: Team | null;
  signalsResource?: boolean;
  teamPermission?: string;
  allowEither?: boolean;
}

export const useAuthorizationLogic = ({
  requiredPermission,
  team = null,
  signalsResource = false,
  teamPermission,
  allowEither = false,
}: UseAuthorizationLogicParams): AuthorizationState => {
  const {
    isAuthorized: isOrgAuthorized,
    isLoading: isOrgLoading,
    isError: isOrgError,
    displayName: orgDisplayName,
  } = useAuthorizationV2(requiredPermission);

  // Use teamPermission if provided, otherwise fall back to requiredPermission
  const permissionForTeamCheck = teamPermission || requiredPermission;

  const {
    isAuthorized: isTeamAuthorized,
    isLoading: isTeamLoading,
    isError: isTeamError,
    displayName: teamDisplayName,
  } = useTeamAuthorizationV2(permissionForTeamCheck, team, signalsResource);

  return useMemo(() => {
    // If no team is provided, use organization authorization
    if (!team?.id) {
      return {
        isAuthorized: isOrgAuthorized,
        isLoading: isOrgLoading,
        isError: isOrgError,
        orgPermissionDisplayName: orgDisplayName,
        teamPermissionDisplayName: undefined,
        isTeamPermission: false,
      };
    }

    // If allowEither is true, check if either organization or team authorization is successful
    if (allowEither) {
      const isAuthorized = isOrgAuthorized || isTeamAuthorized;
      const isLoading = isOrgLoading || isTeamLoading;
      const isError = isOrgError || isTeamError;

      return {
        isAuthorized,
        isLoading,
        isError,
        orgPermissionDisplayName: orgDisplayName,
        teamPermissionDisplayName: teamDisplayName,
        isTeamPermission: isTeamAuthorized, // Indicate if team permission was the successful one
      };
    }

    // For signals resources, check if team restricts signals resource management
    if (signalsResource) {
      if (team.restrict_signals_resource_management) {
        return {
          isAuthorized: isTeamAuthorized,
          isLoading: isTeamLoading,
          isError: isTeamError,
          orgPermissionDisplayName: orgDisplayName,
          teamPermissionDisplayName: teamDisplayName,
          isTeamPermission: true,
        };
      } else {
        return {
          isAuthorized: isOrgAuthorized,
          isLoading: isOrgLoading,
          isError: isOrgError,
          orgPermissionDisplayName: orgDisplayName,
          teamPermissionDisplayName: teamDisplayName,
          isTeamPermission: false,
        };
      }
    }

    // For non-signals resources, always use team authorization when team exists
    return {
      isAuthorized: isTeamAuthorized,
      isLoading: isTeamLoading,
      isError: isTeamError,
      orgPermissionDisplayName: orgDisplayName,
      teamPermissionDisplayName: teamDisplayName,
      isTeamPermission: true,
    };
  }, [
    isOrgAuthorized,
    isOrgLoading,
    isOrgError,
    orgDisplayName,
    isTeamAuthorized,
    isTeamLoading,
    isTeamError,
    teamDisplayName,
    team,
    signalsResource,
    permissionForTeamCheck,
    allowEither,
  ]);
};
